import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function LeadFormDialog({ open, setOpen, stage, users, onCreated }) {
  const [data, setData] = useState({});
  const [assignedTo, setAssignedTo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setData({});
      setAssignedTo("");
    }
  }, [open]);

  const submit = async () => {
    if (!stage) return;
    setSaving(true);
    try {
      await api.post("/leads", { stage_id: stage.id, data, assigned_to: assignedTo || null });
      toast.success("Lead created");
      onCreated();
      setOpen(false);
    } catch (e) {
      toast.error("Failed to create lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-lead-button" className="bg-primary hover:bg-[#0056B3]">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          New lead
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-border max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">New lead — {stage?.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(stage?.fields || []).map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">
                {f.label}
              </label>
              {f.type === "select" ? (
                <Select value={data[f.key] || ""} onValueChange={(v) => setData((d) => ({ ...d, [f.key]: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-border">
                    <SelectValue placeholder={`Select ${f.label}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <textarea
                  value={data[f.key] || ""}
                  onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))}
                  rows={3}
                  className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
                />
              ) : (
                <Input
                  type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                  value={data[f.key] || ""}
                  onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))}
                  className="bg-[#0A0A0A] border-border"
                />
              )}
            </div>
          ))}
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">
              Assign to
            </label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger className="bg-[#0A0A0A] border-border">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                {users.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-border bg-transparent">
            Cancel
          </Button>
          <Button data-testid="save-lead-button" onClick={submit} disabled={saving} className="bg-primary hover:bg-[#0056B3]">
            {saving ? "Saving..." : "Create lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
